import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useMemo, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useDashboardParams } from "@/hooks/use-dashboard-params";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";

import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Card } from "@/components/ui/card";
import { ErrorBanner } from "@/components/ui/error-banner";
import { Icon } from "@/components/ui/icons";
import { KPICard } from "@/components/ui/kpi-card";
import { HStack } from "@/components/ui/h-stack";
import { ResponsiveGrid } from "@/components/ui/responsive-grid";
import { Surface } from "@/components/ui/surface";
import { VStack } from "@/components/ui/v-stack";
import { PageTitle } from "@/contexts/page-title-context";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { useSizeClass } from "@/hooks/use-size-class";

import { getAdminDashboardStats } from "@/lib/api/admin/dashboard";
import {
  ActionQueueCards,
  ActionQueueTreeTable,
  DetailPanelRouter,
  ResolutionDialog,
  ScopeToggle,
  type Scope,
  type TreeSelection,
  type GroupedItem,
} from "@/components/dashboard";
import { EscalateDialog } from "@/components/shared/escalate-dialog";
import { useTicketDialogs } from "@/hooks/use-ticket-dialogs";

function AdminKpiSkeletonGrid({
  labels,
  icons,
}: {
  labels: string[];
  icons: React.ReactNode[];
}) {
  return (
    <ResponsiveGrid cols={5} gap="sm">
      {labels.map((label, index) => (
        <Card key={label} variant="bordered" padding="sm">
          <HStack align="start" justify="between" className="mb-1.5">
            <Text size="xs" color="muted" weight="medium">
              {label}
            </Text>
            <Surface
              bg="primary-container"
              rounded="lg"
              className="flex items-center p-1.5 text-[color:var(--primary)]"
            >
              {icons[index]}
            </Surface>
          </HStack>
          <Skeleton className="mb-0.5 h-7 w-12" />
          <Skeleton className="h-3 w-24" />
        </Card>
      ))}
    </ResponsiveGrid>
  );
}

function useWorkAreaRefresh() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [locallyViewedIds, setLocallyViewedIds] = useState<Set<string>>(
    new Set(),
  );
  const handleRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const handleMarkViewed = useCallback((ticketId: string) => {
    setLocallyViewedIds((prev) => {
      const next = new Set(prev);
      next.add(ticketId);
      return next;
    });
  }, []);
  return { refreshKey, locallyViewedIds, handleRefresh, handleMarkViewed };
}

function AdminDashboardPageInner() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const { t: tAdminDashboard } = useTranslation("admin.dashboard");
  const { t: tCommon } = useTranslation("common");
  const { t: tDashboard } = useTranslation("dashboard");

  const {
    data: stats,
    isLoading: loading,
    error: statsError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: queryKeys.admin.dashboardStats(),
    queryFn: getAdminDashboardStats,
  });

  const {
    initialQueue,
    initialTicket,
    initialGroup,
    setQueue,
    setSelection: setUrlSelection,
  } = useDashboardParams();

  const { filters: scopeFilters, setFilter: setScopeFilter } =
    useFilterSearchParams({
      keys: ["scope"],
      defaults: { scope: "all" },
    });
  const scope = scopeFilters.scope as Scope;

  const [selection, setSelection] = useState<TreeSelection | null>(() => {
    if (initialGroup && !initialTicket)
      return { group_id: initialGroup, ticket_id: null };
    return null;
  });
  const [adminGroups, setAdminGroups] = useState<GroupedItem[]>([]);

  const ticketTypeFilter = initialQueue;

  const {
    refreshKey,
    locallyViewedIds,
    handleRefresh: handleRefreshAdmin,
    handleMarkViewed,
  } = useWorkAreaRefresh();

  const selectedTicketId = selection?.ticket_id ?? initialTicket ?? null;

  const handleSelectItem = useCallback(
    (sel: TreeSelection | null) => {
      setSelection(sel);
      setUrlSelection(sel?.ticket_id ?? undefined, sel?.group_id ?? undefined);
    },
    [setUrlSelection],
  );

  const handleFilterByType = useCallback(
    (type: string) => {
      const next = ticketTypeFilter === type ? undefined : type;
      setQueue(next, selection?.ticket_id ?? undefined);
    },
    [setQueue, selection, ticketTypeFilter],
  );

  const {
    escalationDialog: adminEscDialog,
    resolutionDialog: adminResDialog,
    handleEscalate: adminHandleEscalate,
    handleResolve: adminHandleResolve,
    closeEscalation: adminCloseEscalation,
    closeResolution: adminCloseResolution,
    handleEscalationSuccess: adminEscSuccess,
    handleResolutionSuccess: adminResSuccess,
  } = useTicketDialogs(handleRefreshAdmin);

  const kpis = useMemo(() => {
    return [
      {
        title: tAdminDashboard("kpis.awaitingApplications"),
        value: stats?.awaiting_application_count ?? 0,
        icon: <Icon.FileText size="sm" />,
      },
      {
        title: tAdminDashboard("kpis.pendingLoads"),
        value: stats?.pending_load_count ?? 0,
        icon: <Icon.Loads size="sm" />,
      },
      {
        title: tAdminDashboard("kpis.openEscalations"),
        value: stats?.open_escalations_count ?? 0,
        icon: <Icon.Alert size="sm" />,
      },
      {
        title: tAdminDashboard("kpis.unassignedTickets"),
        value: stats?.unassigned_tickets_count ?? 0,
        icon: <Icon.Ticket size="sm" />,
      },
      {
        title: tAdminDashboard("kpis.resolvedToday"),
        value: stats?.resolved_today_count ?? 0,
        icon: <Icon.CheckCircle size="sm" />,
      },
    ];
  }, [stats, tAdminDashboard]);

  return (
    <VStack
      gap="sm"
      className="lg:h-[calc(100vh-8rem)]"
      data-testid="admin-dashboard-page"
    >
      <PageTitle>{tAdminDashboard("title")}</PageTitle>

      {/* {tDashboard("portfolioHealth")} */}
      <Text color="muted" weight="semibold" className="pl-3">
        {tDashboard("portfolioHealth")}
      </Text>

      {statsError ? (
        <ErrorBanner
          title={tAdminDashboard("failedToLoadDashboard")}
          onRetry={() => void refetchDashboard()}
          retryLabel={tCommon("retry")}
        />
      ) : null}

      {loading ? (
        <AdminKpiSkeletonGrid
          labels={kpis.map((kpi) => kpi.title)}
          icons={kpis.map((kpi) => kpi.icon)}
        />
      ) : (
        <ResponsiveGrid cols={isCompact ? 2 : 5} gap="sm">
          {kpis.map((kpi) => (
            <KPICard
              compact
              key={kpi.title}
              title={kpi.title}
              value={kpi.value}
              icon={kpi.icon}
              iconColor="role"
              cardVariant="bordered"
            />
          ))}
        </ResponsiveGrid>
      )}

      {/* {tDashboard("myActionQueue")} */}
      <VStack gap="sm">
        <HStack gap="lg">
          <Text color="muted" weight="semibold" className="pl-3">
            {tDashboard("myActionQueue")}
          </Text>
          <ScopeToggle
            value={scope}
            onChange={(v) => setScopeFilter("scope", v)}
            showTeam
          />
        </HStack>

        <ActionQueueCards
          role="Admin"
          scope={scope}
          activeFilter={ticketTypeFilter}
          onFilterByType={handleFilterByType}
          data-testid="admin-action-queue"
        />
      </VStack>

      {/* Work Table + Detail Panel */}
      {isCompact ? (
        <VStack data-testid="admin-dashboard-work-area" className="flex-1">
          <Card
            variant="bordered"
            padding="none"
            className="flex flex-1 flex-col overflow-hidden"
          >
            <ActionQueueTreeTable
              role="Admin"
              scope={scope}
              ticketTypeFilter={ticketTypeFilter}
              selectedTicketId={selectedTicketId}
              selectedGroupId={selection?.group_id ?? initialGroup ?? null}
              onSelectItem={handleSelectItem}
              onGroupsLoaded={setAdminGroups}
              viewedTicketIds={locallyViewedIds}
              refreshKey={refreshKey}
            />
          </Card>
          <AdaptiveSheet
            open={!!selectedTicketId || !!selection?.group_id}
            onDismiss={() => handleSelectItem(null)}
            detents={["medium", "large"]}
          >
            <DetailPanelRouter
              selection={selection}
              groups={adminGroups}
              onRefresh={handleRefreshAdmin}
              onMarkViewed={handleMarkViewed}
              viewedTicketIds={locallyViewedIds}
              onEscalate={(id) => void adminHandleEscalate(id)}
              onResolve={(id) => void adminHandleResolve(id)}
            />
          </AdaptiveSheet>
        </VStack>
      ) : (
        // eslint-disable-next-line design-tokens/no-raw-html-elements -- CSS grid layout with responsive column template
        <div
          data-testid="admin-dashboard-work-area"
          className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-3 min-h-0"
        >
          <HStack align="stretch" className="lg:col-span-2 overflow-hidden">
            <Card
              variant="bordered"
              padding="none"
              className="flex flex-1 flex-col overflow-hidden"
            >
              <ActionQueueTreeTable
                role="Admin"
                scope={scope}
                ticketTypeFilter={ticketTypeFilter}
                selectedTicketId={selectedTicketId}
                selectedGroupId={selection?.group_id ?? initialGroup ?? null}
                onSelectItem={handleSelectItem}
                onGroupsLoaded={setAdminGroups}
                viewedTicketIds={locallyViewedIds}
                refreshKey={refreshKey}
              />
            </Card>
          </HStack>
          <VStack className="lg:col-span-1 overflow-hidden">
            <DetailPanelRouter
              selection={selection}
              groups={adminGroups}
              onRefresh={handleRefreshAdmin}
              onMarkViewed={handleMarkViewed}
              viewedTicketIds={locallyViewedIds}
              onEscalate={(id) => void adminHandleEscalate(id)}
              onResolve={(id) => void adminHandleResolve(id)}
            />
          </VStack>
        </div>
      )}

      {adminEscDialog ? (
        <EscalateDialog
          ticketId={adminEscDialog.ticketId}
          ticketDisplayId={adminEscDialog.ticketDisplayId}
          onClose={adminCloseEscalation}
          onSuccess={adminEscSuccess}
        />
      ) : null}
      {adminResDialog ? (
        <ResolutionDialog
          ticketId={adminResDialog.ticketId}
          ticketDisplayId={adminResDialog.ticketDisplayId}
          actorType={adminResDialog.actorType}
          escalationChain={adminResDialog.escalationChain}
          parentTicket={adminResDialog.parentTicket}
          onClose={adminCloseResolution}
          onSuccess={adminResSuccess}
        />
      ) : null}
    </VStack>
  );
}

function AdminDashboardPage() {
  return (
    <Suspense>
      <AdminDashboardPageInner />
    </Suspense>
  );
}

export const Route = createFileRoute("/_app/admin/dashboard")({
  component: AdminDashboardPage,
});
